import React from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { AnswerLabel, OpenDetailButton, useAnswerLabel } from './ArchiveBodies'
import ExpandableText from './ExpandableText'

export const isDayAnswered = (day) => day.answered ?? day.answers?.length > 0

// The most recently written day, or null.
export const latestJournalDay = (days = []) => days.reduce((best, day) => (day.answered_at && (!best || day.answered_at > best.answered_at) ? day : best), null)

export const formatDay = (ms) => {
    try {
        return ms ? format(ms, 'dd MMM yyyy') : ''
    } catch (e) {
        return ''
    }
}

/**
 * Journal summary card: name, progress across all days and the most recently written day.
 * For staff (`answersHidden`) the student's words are withheld by the API, so the card
 * shows when they wrote instead, with a note that the content is private.
 */
export default function JournalBody({ content, response, onOpen, answersHidden }) {
    const { t } = useTranslation()
    const answerLabel = useAnswerLabel()
    const days = response.days || []
    const total = response.total ?? days.length
    const answered = response.answered ?? days.filter(isDayAnswered).length
    const latest = latestJournalDay(days)
    const name = content?.name || content?.title

    return (
        <>
            {!!name && <h3 className="archive-title">{name}</h3>}
            <ExpandableText text={content?.message || content?.description} lines={2} className="archive-description" />
            {total > 0 && (
                <div className="archive-progress">
                    <span className="archive-progress-track">
                        <span className="archive-progress-fill" style={{ transform: `scaleX(${answered / total})` }} />
                    </span>
                    <span className="archive-progress-text">{t('archiveJournalProgress', { answered, total })}</span>
                </div>
            )}
            {!!latest && (
                <div className="archive-journal-latest">
                    <p className="archive-journal-latest-head">
                        <span className="archive-jday-number is-answered">
                            {t('day')} {latest.number + 1}
                        </span>
                        {!!(latest.title || latest.question) && <span className="archive-journal-latest-title">{latest.title || latest.question}</span>}
                    </p>
                    {!!latest.title && !!latest.question && <p className="archive-prompt">{latest.question}</p>}
                    {answersHidden ? (
                        <p className="archive-private-note">
                            {t('archiveJournalWrittenOn')} {formatDay(latest.answered_at)} · {t('archiveJournalPrivateNote')}
                        </p>
                    ) : (
                        <>
                            <AnswerLabel>{answerLabel(latest.answers.length)}</AnswerLabel>
                            <ol className="archive-journal-answers">
                                {latest.answers.map((answer, index) => (
                                    <li key={index} className="archive-journal-answer">
                                        <span className="archive-survey-number" aria-hidden="true">
                                            {index + 1}
                                        </span>
                                        <span>{answer}</span>
                                    </li>
                                ))}
                            </ol>
                        </>
                    )}
                </div>
            )}
            <OpenDetailButton onClick={onOpen}>{t('archiveOpenJournal')}</OpenDetailButton>
        </>
    )
}
